import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Button variants (CVA)
   -------------------------------------------------------------------------- */

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-[family-name:var(--font-heading)] font-semibold',
    'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
    'select-none whitespace-nowrap',
    'disabled:pointer-events-none disabled:opacity-45',
    'active:scale-[0.97]',
    'cursor-pointer',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-accent-primary text-text-inverted',
          'hover:bg-accent-primary-hover',
          'active:bg-accent-primary-active',
          'shadow-sm hover:shadow-md',
        ].join(' '),
        secondary: [
          'bg-transparent text-accent-primary',
          'border-2 border-accent-primary',
          'hover:bg-accent-primary-light',
          'active:bg-accent-primary-medium',
        ].join(' '),
        ghost: [
          'bg-transparent text-text-primary',
          'hover:bg-surface-sunken',
          'active:bg-border-default',
        ].join(' '),
        danger: [
          'bg-status-negative text-text-inverted',
          'hover:bg-[#8C4626]',
          'active:bg-[#7A3D21]',
          'shadow-sm hover:shadow-md',
        ].join(' '),
      },
      size: {
        sm: 'h-9 px-3.5 text-sm rounded-[var(--radius-sm)]',
        md: 'h-11 px-5 text-base rounded-[var(--radius-md)]',
        lg: 'h-14 px-7 text-lg rounded-[var(--radius-md)]',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Show loading skeleton pulse instead of content. */
  loading?: boolean;
  /** Icon rendered before children. */
  leftIcon?: ReactNode;
  /** Icon rendered after children. */
  rightIcon?: ReactNode;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="skeleton h-4 w-20 rounded-[var(--radius-sm)]" />
          </span>
        ) : (
          <>
            {leftIcon && (
              <span className="shrink-0 [&>svg]:h-[1.1em] [&>svg]:w-[1.1em]">
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span className="shrink-0 [&>svg]:h-[1.1em] [&>svg]:w-[1.1em]">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { buttonVariants };
